import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Scale, FileText, BookOpen, ScrollText } from 'lucide-react';
import EmbeddedModule from './EmbeddedModule';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { key: 'all', label: 'All', icon: BookOpen },
  { key: 'writ', label: 'Writs', icon: Scale },
  { key: 'contract', label: 'Contracts', icon: FileText },
  { key: 'draft', label: 'Drafts', icon: ScrollText },
  { key: 'template', label: 'Templates', icon: FileText }
];

const LegalTechHub = () => {
  const [docs, setDocs] = useState([]);
  const [stats, setStats] = useState(null);
  const [type, setType] = useState('all');
  const [q, setQ] = useState('');

  useEffect(() => {
    axios.get(`${API}/legal-docs`).then((r) => setDocs(r.data)).catch(() => setDocs([]));
    axios.get(`${API}/legal-docs/stats`).then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);

  const filtered = useMemo(() => {
    return docs
      .filter((d) => (type === 'all' ? true : d.doc_type === type))
      .filter((d) => {
        const s = q.trim().toLowerCase();
        if (!s) return true;
        return [d.title, d.summary, ...(d.tags || [])].join(' ').toLowerCase().includes(s);
      });
  }, [docs, type, q]);

  return (
    <EmbeddedModule
      eyebrow="Legal Tech · India"
      title="Legal Tech Hub"
      tagline="IndiaKanoon-style repository for the regulated stack — writ petitions, contracts, drafts, ready-to-deploy templates and compliance precedents."
      badgeText="Ready-to-deploy library"
      accentClass="from-slate-800 via-slate-700 to-zinc-700"
      testId="legal-tech-page"
      kpis={[
        { label: 'Documents', value: stats?.total ?? '—' },
        { label: 'Writs', value: stats?.by_type?.writ ?? 0 },
        { label: 'Contracts', value: stats?.by_type?.contract ?? 0 },
        { label: 'Templates', value: stats?.by_type?.template ?? 0 }
      ]}
    >
      <Card data-testid="legal-tech-list">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle>Library</CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by title, tag, summary..."
                className="h-9 w-72"
                data-testid="legal-search"
              />
              <div className="flex items-center gap-1">
                {TYPES.map((t) => (
                  <button
                    key={t.key}
                    data-testid={`legal-filter-${t.key}`}
                    onClick={() => setType(t.key)}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                      type === t.key
                        ? 'bg-slate-800 text-white border-slate-800'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-slate-300'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filtered.map((d) => (
              <div
                key={d.id}
                className="border border-gray-200 rounded-lg p-4 bg-white hover:border-slate-400 hover:shadow-sm transition-all"
                data-testid={`legal-doc-${d.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <Badge variant="outline" className="text-[10px] uppercase">{d.doc_type}</Badge>
                  {d.ready_template && (
                    <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Ready</Badge>
                  )}
                </div>
                <p className="text-sm font-semibold text-gray-900">{d.title}</p>
                {d.court && (
                  <p className="text-[11px] text-gray-500 mt-0.5">Court: {d.court}</p>
                )}
                <p className="text-xs text-gray-600 mt-2">{d.summary}</p>
                <div className="flex flex-wrap gap-1 mt-3">
                  {(d.tags || []).map((t) => (
                    <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-6">No documents found.</p>
          )}
        </CardContent>
      </Card>
    </EmbeddedModule>
  );
};

export default LegalTechHub;
